import { getEmbedConfig } from '../data';

/**
 * GCP Logs Explorer expects comma-separated path segments with `/` double-encoded (%252F) but commas left literal.
 * See working console URLs (e.g. lfeCustomFields=jsonPayload%252FeventType,jsonPayload%252Fmessage).
 */
function encodeGcpLogsExplorerLfeCustomFields(value: string): string {
  return value
    .split(',')
    .map((segment) => encodeURIComponent(segment.trim()).replace(/%2F/g, '%252F'))
    .join(',');
}

/** summaryFields uses %252F for slashes and literal colons after one decode (not %3A). */
function encodeGcpLogsExplorerSummaryFields(value: string): string {
  return encodeURIComponent(value).replace(/%2F/g, '%252F').replace(/%3A/g, ':');
}

/**
 * Log query param: encodeURIComponent does not escape `(` `)` (URI "unescaped" set), so they stay literal.
 * GCP Logs Explorer share links expect them as %2528 / %2529 instead.
 */
export function encodeGcpLogsExplorerQuery(logQuery: string): string {
  return encodeURIComponent(logQuery).replace(/\(/g, '%2528').replace(/\)/g, '%2529');
}

const TRIMBLE_CLOUD_LOGS_RESOURCE_TYPE = 'k8s_container';
const TRIMBLE_CLOUD_LOGS_K8S_NAMESPACE = 'sixfold';
const TRIMBLE_CLOUD_LOGS_CONTAINER = 'trimble-cloud-int-service';
const TRIMBLE_CLOUD_LOGS_LFE_FIELDS = 'jsonPayload/eventType,jsonPayload/message';
const TRIMBLE_CLOUD_LOGS_SUMMARY_FIELDS = 'jsonPayload/applicationPropertiesInput/EventType:false:32:beginning';
const TRIMBLE_LOGS_WINDOW_PADDING_MS = 60 * 60 * 1000;

type TrimbleTransportCloudLogsTourRefs = {
  tour_id: number;
  transport_id: string | null | undefined;
  shipper_transport_number: string | null;
  platform_transport_number: string | null;
  created_at: string;
  complete_time: string | null;
  abort_time: string | null;
};

function parseTourIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function resolveTrimbleLogsExplorerInterval(
  tour: Pick<TrimbleTransportCloudLogsTourRefs, 'created_at' | 'complete_time' | 'abort_time'>,
): { start: Date; end: Date } {
  const created = parseTourIsoDate(tour.created_at) ?? new Date();
  let start = new Date(created.getTime() - TRIMBLE_LOGS_WINDOW_PADDING_MS);

  const end = parseTourIsoDate(tour.complete_time) ?? parseTourIsoDate(tour.abort_time) ?? new Date();

  if (start.getTime() >= end.getTime()) {
    start = new Date(end.getTime() - TRIMBLE_LOGS_WINDOW_PADDING_MS);
  }

  return { start, end };
}

function collectTrimbleCloudLogSearchTerms(tour: TrimbleTransportCloudLogsTourRefs): string[] {
  const terms = new Set<string>([String(tour.tour_id)]);

  const transportId = (tour.transport_id ?? '').trim();
  if (transportId) {
    terms.add(transportId);
  }

  const shipperTn = tour.shipper_transport_number?.trim();
  if (shipperTn) {
    terms.add(shipperTn);
  }

  const platformTn = tour.platform_transport_number?.trim();
  if (platformTn) {
    const i = platformTn.lastIndexOf('#');
    const afterHash = i >= 0 ? platformTn.slice(i + 1).trim() : '';
    if (afterHash) {
      terms.add(afterHash);
    }
  }

  return Array.from(terms);
}

function quoteLogsExplorerOrTerm(term: string): string {
  return `"${term.replace(/"/g, '')}"`;
}

function buildTrimbleCloudLogsQuery(tour: TrimbleTransportCloudLogsTourRefs): string {
  const resourceLine = [
    `resource.type="${TRIMBLE_CLOUD_LOGS_RESOURCE_TYPE}"`,
    `resource.labels.namespace_name="${TRIMBLE_CLOUD_LOGS_K8S_NAMESPACE}"`,
    `(resource.labels.container_name="${TRIMBLE_CLOUD_LOGS_CONTAINER}")`,
  ].join(' ');
  const orLine = `(${collectTrimbleCloudLogSearchTerms(tour).map(quoteLogsExplorerOrTerm).join(' OR ')})`;
  return `${resourceLine}\n${orLine}\n`;
}

/** Opens GCP Logs Explorer for trimble-cloud-int-service with tour identifiers in the text filter (see TTC backoffice links). */
export function buildTrimbleTransportCloudLogsExplorerUrl(tour: TrimbleTransportCloudLogsTourRefs): string {
  const gcpProject = getEmbedConfig()?.gcp_logs_explorer_project;
  const { start, end } = resolveTrimbleLogsExplorerInterval(tour);
  const logQuery = buildTrimbleCloudLogsQuery(tour);
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  const pathSegment = [
    `cursorTimestamp=${endIso}`,
    `endTime=${endIso}`,
    `lfeCustomFields=${encodeGcpLogsExplorerLfeCustomFields(TRIMBLE_CLOUD_LOGS_LFE_FIELDS)}`,
    `query=${encodeGcpLogsExplorerQuery(logQuery)}`,
    `startTime=${startIso}`,
    `summaryFields=${encodeGcpLogsExplorerSummaryFields(TRIMBLE_CLOUD_LOGS_SUMMARY_FIELDS)}`,
  ].join(';');

  return `https://console.cloud.google.com/logs/query;${pathSegment}?project=${gcpProject}`;
}
